<script setup lang="ts">
import Typography from '@components/Typography.vue';
import CheckableTagGroup from '@pages/search/components/filters/CheckableTagGroup.vue';
import { trans } from '@tools/translation.ts';
import { useSearchStore } from '@stores/useSearchStore.ts';
import { storeToRefs } from 'pinia';
import type { TransportLine } from '@models/groups/search';
import { computed } from 'vue';
import type { TransportLine as TransportLineEnum } from '@models/types/transportLine';
import { getImageUrl } from '@tools/media-image';

interface TransportsProps {
  selected?: string[];
}

const { selected = [] } = defineProps<TransportsProps>();

const { transportLines } = storeToRefs(useSearchStore());

const emit = defineEmits<{(e: 'update:selected', value: string[]): void}>();

interface TransportLineFormatted {
  code: string,
  label: string,
  exponent: string,
}

type TransportLinesByType = Record<TransportLineEnum, TransportLineFormatted[]>;

const getFormattedTransportLine = ({ name, type }: TransportLine): {
  code: string,
  label: string,
  exponent: string,
} => ({
  code: `${type.toLowerCase()}-${name}`,
  label: `${trans('global.line')} ${name.split('-')[0]}`,
  exponent: name.split('-')[1],
});

const formattedTransportLinesByTypes = computed<TransportLinesByType>(() => {
  const transportLinesByType: TransportLinesByType = {
    metro: [],
    rer: [],
    tram: [],
    transilien: [],
  };

  transportLines.value.forEach((transportLine) => transportLinesByType[transportLine.type].push(getFormattedTransportLine(transportLine)));

  return transportLinesByType;
});

const iconImg = computed<string>(() => getImageUrl('f13b1efb-2744-43a1-bc0d-d0ed4b5f96e8'));
</script>

<template>
  <div>
    <div
      v-for="(formattedTransportLinesByType, type) in formattedTransportLinesByTypes"
      :key="type"
    >
      <div class="flex items-center gap-x-2">
        <Typography
          variant="body"
          class="py-3"
        >
          {{ trans(`global.ratp-${type.toLocaleLowerCase()}`) }}
        </Typography>

        <img
          :src="iconImg"
          :class="`icon-ratp-${type}`"
          :alt="type"
        >
      </div>

      <CheckableTagGroup
        class="grid grid-cols-3 pb-4"
        :options="formattedTransportLinesByType"
        :selected="selected"
        @update:selected="emit('update:selected', $event)"
      >
        <template #before="{ code }">
          <img
            :src="iconImg"
            :class="`icon-ratp-${code}-big`"
            :alt="code"
          >
        </template>
      </CheckableTagGroup>
    </div>
  </div>
</template>
